import {
  Alert,
  Button,
  Card,
  Select,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import { FC, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import { actions as usersActions } from '@/api/users';
import { useAccount } from '@/api/rest/users/useAccount';

import { setPrimaryPipeline } from '@payaca/store/account/accountActions';

type Props = {
  readOnly: boolean;
};

const CompanySettingsPrimaryPipeline: FC<Props> = ({ readOnly }) => {
  const dispatch = useDispatch();
  const { data: pipelines = [] } = useGetMyAccountPipelines();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const account = useAccount();
  const [primaryPipelineId, setPrimaryPipelineId] = useState<number>(
    account.primaryPipelineId || 0
  );

  const options = useMemo(() => {
    return pipelines.map((x) => {
      return { label: x.title, value: Number(x.id) };
    });
  }, [pipelines]);

  const selectedOption = options.find((x) => x.value === primaryPipelineId);

  const onSubmit = () => {
    setIsSuccess(false);
    setIsError(false);
    setIsProcessing(true);
    dispatch(
      setPrimaryPipeline.request({
        pipelineId: primaryPipelineId || null,
        callback: () => {
          dispatch(usersActions.getProfile());
          setIsProcessing(false);
          setIsSuccess(true);
        },
        onErrorCallback: () => {
          setIsProcessing(false);
          setIsError(true);
        },
      })
    );
  };

  return (
    <Card sizeVariant="lg">
      <Card.Body className="space-y-4">
        <div className="flex gap-4">
          <Select
            sizeVariant={SelectSizeVariant.SM}
            className="max-w-64"
            options={options}
            value={primaryPipelineId}
            onChange={setPrimaryPipelineId}
            disabled={readOnly}
            multiple={false}
          />
          {!readOnly && (
            <Button
              size={'sm'}
              variant="secondary"
              onClick={isProcessing ? undefined : onSubmit}
              isProcessing={isProcessing}
              disabled={primaryPipelineId == account.primaryPipelineId}
            >
              Save
            </Button>
          )}
        </div>

        {primaryPipelineId != account.primaryPipelineId && (
          <>
            {!!primaryPipelineId && !account.primaryPipelineId && (
              <Alert colour="yellow">
                <ul>
                  <li>
                    The Payaca <strong>Default</strong> pipeline will no longer
                    be visible for you
                  </li>
                  <li>
                    Any projects currently in the <strong>Default</strong>{' '}
                    pipeline will be moved to{' '}
                    <strong>{selectedOption?.label}</strong>
                  </li>
                  <li>
                    Any automation conditions currently referencing the{' '}
                    <strong>Default</strong> pipeline will be changed to
                    reference <strong>{selectedOption?.label}</strong>. You may
                    need to revisit these automations to check that they are
                    still useful.
                  </li>
                </ul>
              </Alert>
            )}
            {account.primaryPipelineId && (
              <Alert colour="yellow">
                <ul>
                  {!primaryPipelineId && (
                    <li>
                      The Payaca <strong>Default</strong> pipeline will be added
                      to your account.
                    </li>
                  )}
                  <li>
                    No projects will move pipeline as a result of this change.
                  </li>
                </ul>
              </Alert>
            )}
          </>
        )}

        {isSuccess && <Alert colour="green">Save successful!</Alert>}
        {isError && (
          <Alert colour="red">
            Something went wrong updating the primary pipeline
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsPrimaryPipeline;
